// src/components/admin/CheckinPermissionsAccordion.jsx
//
// Super Admin accordion for the Student CHECK-IN system. Mirrors the
// CheckoutPermissionsAccordion (in SuperAdminPanel.js) but for the start-of-
// year check-in checklist. For each checklist item it lets a superadmin set:
//
//   • Staff allowed to check the item off (signers)
//   • Department — free text, used for contact / "who to ask"
//   • Audience — who the item pertains to (All / Day / Boarding / International)
//   • Required — whether the item is mandatory for full check-in
//
// All four are persisted to /config/checkinPointPeople with shape:
//
//   {
//     items: {
//       finance:  { signers: [uid,...], department: 'Finance', audience: ['all'], required: true },
//       handbook: { ... },
//       ...
//     }
//   }
//
// The item list and DEFAULT department/audience/required values live in
// src/config/checkinItems.js (CHECKIN_ITEMS). This accordion merges those
// defaults with the Firestore overrides via getResolvedCheckinItems(), so any
// item the admin hasn't touched still renders with sensible defaults — and the
// first edit persists the complete item object.

import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  CHECKIN_ITEMS,
  CHECKIN_AUDIENCES,
  CHECKIN_CONFIG_DOC,
  getResolvedCheckinItems,
  normalizeAudience,
} from '../../config/checkinItems';

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  accordion: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    marginBottom: '1rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  header: (open) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', cursor: 'pointer', userSelect: 'none',
    background: open ? '#fafafa' : '#fff',
    borderBottom: open ? '1px solid #f3f4f6' : 'none',
  }),
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  icon: { fontSize: 18 },
  chevron: { fontSize: 12, color: '#9ca3af', transition: 'transform 0.2s' },
  title: { fontSize: 15, fontWeight: 700, color: 'var(--ka-navy)', margin: 0 },
  subtitle: { fontSize: 12, color: '#6b7280', margin: '2px 0 0' },
  body: { padding: '16px 18px' },
  intro: {
    fontSize: 13, color: '#444', lineHeight: 1.5,
    marginBottom: 16, padding: 12,
    background: 'rgba(26,54,93,0.04)',
    borderLeft: '3px solid var(--ka-navy)',
    borderRadius: 4,
  },
  itemRow: {
    padding: '16px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  itemRowLast: { borderBottom: 'none' },
  itemHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 12, flexWrap: 'wrap', marginBottom: 10,
  },
  itemLabel: { fontSize: 15, fontWeight: 700, color: 'var(--ka-navy)', margin: 0 },
  itemDesc: { fontSize: 11, color: '#888', margin: '2px 0 0', fontStyle: 'italic' },
  requiredWrap: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  requiredLabel: (req) => ({
    fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
    color: req ? '#C62828' : '#9ca3af', textTransform: 'uppercase',
  }),
  fieldLabel: {
    fontSize: 11, fontWeight: 700, color: 'var(--ka-navy)',
    textTransform: 'uppercase', letterSpacing: 0.4, margin: '12px 0 5px',
  },
  fieldLabelGold: {
    fontSize: 11, fontWeight: 700, color: 'var(--ka-gold)',
    textTransform: 'uppercase', letterSpacing: 0.4, margin: '12px 0 5px',
  },
  twoCol: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 14, marginBottom: 4,
  },
  deptInput: {
    width: '100%', padding: '7px 10px', fontSize: 13,
    border: '1px solid #d1d5db', borderRadius: 6, outline: 'none',
    boxSizing: 'border-box',
  },
  audienceRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  audiencePill: (on) => ({
    padding: '5px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', border: '1px solid',
    borderColor: on ? 'var(--ka-navy)' : '#d1d5db',
    background: on ? 'var(--ka-navy)' : '#fff',
    color: on ? '#fff' : '#555',
    transition: 'all 0.12s',
  }),
  // Toggle (required)
  toggle: {
    position: 'relative', width: 36, height: 20, borderRadius: 10,
    border: 'none', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
  },
  toggleKnob: {
    position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%',
    background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  saveStatus: { marginTop: 12, padding: 8, fontSize: 12, borderRadius: 4 },
  saveStatusSuccess: { background: '#D4EDDA', color: '#155724' },
  saveStatusError: { background: '#FFEBEE', color: '#C62828' },
  saveStatusSaving: { background: '#FFF3CD', color: '#856404' },
  // Picker
  pickerWrap: { position: 'relative' },
  pickerRow: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    minHeight: 30, padding: 6,
    background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 6,
  },
  staffPill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'var(--ka-navy)', color: '#fff',
    padding: '3px 6px 3px 10px', borderRadius: 12, fontSize: 12,
  },
  removePillBtn: {
    background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
    width: 16, height: 16, borderRadius: '50%', cursor: 'pointer',
    fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, lineHeight: 1,
  },
  emptyPicker: { fontSize: 11, color: '#999', fontStyle: 'italic', padding: '4px 6px' },
  searchAddInput: {
    flex: 1, minWidth: 140, padding: '4px 8px', border: 'none',
    background: 'transparent', fontSize: 12, outline: 'none',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
    maxHeight: 200, overflowY: 'auto',
    zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', marginTop: 4,
  },
  dropdownItem: {
    padding: '8px 10px', fontSize: 13, cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column',
  },
  dropdownItemHover: { background: 'rgba(26,54,93,0.05)' },
  dropdownEmpty: { padding: '10px', fontSize: 12, color: '#999', fontStyle: 'italic' },
};

// ─── Self-contained multi-select staff picker ────────────────────────────────
// (Duplicated from SuperAdminPanel's StaffPicker to keep this file standalone,
//  matching the codebase's "duplicate small helpers" convention.)

function StaffPicker({ allStaff, selectedUids, onChange, placeholder = 'Add staff…' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hoverId, setHoverId] = useState(null);

  const selectedSet = new Set(selectedUids || []);
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allStaff
      .filter((s) => {
        if (selectedSet.has(s.uid)) return false;
        if (!q) return true;
        const name = (s.displayName || '').toLowerCase();
        const email = (s.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .slice(0, 10);
  }, [allStaff, query, selectedSet]);

  const handleAdd = (uid) => {
    onChange([...(selectedUids || []), uid]);
    setQuery('');
  };
  const handleRemove = (uid) => {
    onChange((selectedUids || []).filter((u) => u !== uid));
  };

  const selectedStaff =
    selectedUids
      ?.map((uid) => allStaff.find((s) => s.uid === uid))
      .filter(Boolean) || [];

  return (
    <div style={styles.pickerWrap}>
      <div style={styles.pickerRow} onClick={() => setOpen(true)}>
        {selectedStaff.length === 0 && !query && (
          <span style={styles.emptyPicker}>None assigned</span>
        )}
        {selectedStaff.map((staff) => (
          <span key={staff.uid} style={styles.staffPill}>
            {staff.displayName || staff.email || staff.uid}
            <button
              type="button"
              style={styles.removePillBtn}
              onClick={(e) => { e.stopPropagation(); handleRemove(staff.uid); }}
              aria-label="Remove"
            >×</button>
          </span>
        ))}
        <input
          type="text"
          style={styles.searchAddInput}
          placeholder={selectedStaff.length === 0 ? placeholder : 'Add another…'}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </div>
      {open && (query || candidates.length > 0) && (
        <div style={styles.dropdown}>
          {candidates.length === 0 ? (
            <div style={styles.dropdownEmpty}>
              {query ? `No staff matching "${query}".` : 'No staff to add.'}
            </div>
          ) : (
            candidates.map((staff) => (
              <div
                key={staff.uid}
                style={{
                  ...styles.dropdownItem,
                  ...(hoverId === staff.uid ? styles.dropdownItemHover : {}),
                }}
                onMouseEnter={() => setHoverId(staff.uid)}
                onMouseLeave={() => setHoverId(null)}
                onMouseDown={(e) => { e.preventDefault(); handleAdd(staff.uid); }}
              >
                <span style={{ fontWeight: 600, color: '#111' }}>
                  {staff.displayName || '(no name)'}
                </span>
                {staff.email && (
                  <span style={{ fontSize: 11, color: '#888' }}>{staff.email}</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Required toggle ──────────────────────────────────────────────────────

function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      style={{ ...styles.toggle, background: on ? 'var(--ka-navy)' : '#d1d5db' }}
      title={on ? 'Required — click to make optional' : 'Optional — click to make required'}
    >
      <div style={{ ...styles.toggleKnob, left: on ? 18 : 2 }} />
    </button>
  );
}

// ─── Department input ─────────────────────────────────────────────────────
// Holds local state so we don't write to Firestore on every keystroke —
// commits on blur or Enter. Re-seeds if the upstream value changes.

function DepartmentInput({ value, onCommit }) {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed !== (value || '')) onCommit(trimmed);
  };

  return (
    <input
      type="text"
      style={styles.deptInput}
      value={draft}
      placeholder="e.g. Finance, Health Center…"
      list="checkin-dept-suggestions"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.currentTarget.blur(); }
        if (e.key === 'Escape') { setDraft(value || ''); e.currentTarget.blur(); }
      }}
    />
  );
}

// ─── Main accordion ───────────────────────────────────────────────────────

export default function CheckinPermissionsAccordion() {
  const [open, setOpen] = useState(false);
  const [allStaff, setAllStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | { error }

  // Load all staff (one-shot) — same merge approach as the checkout accordion.
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'staff'));
        const list = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        const userSnap = await getDocs(collection(db, 'users'));
        const userList = userSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        const merged = new Map();
        userList.forEach((u) => merged.set(u.uid, u));
        list.forEach((s) => merged.set(s.uid, { ...merged.get(s.uid), ...s }));
        const final = Array.from(merged.values()).sort((a, b) =>
          (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''),
        );
        setAllStaff(final);
      } catch (err) {
        console.error('Load staff for check-in picker failed:', err);
      } finally {
        setStaffLoading(false);
      }
    };
    load();
  }, []);

  // Subscribe to the config doc. We don't seed/write until the first edit.
  useEffect(() => {
    const ref = doc(db, 'config', CHECKIN_CONFIG_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setConfig({ items: {} });
        } else {
          const data = snap.data() || {};
          setConfig({
            items: data.items && typeof data.items === 'object' ? { ...data.items } : {},
          });
        }
        setConfigLoading(false);
      },
      (err) => {
        console.error('checkinPointPeople subscribe failed:', err);
        setConfigLoading(false);
      },
    );
    return () => unsub();
  }, []);

  // Resolved items = static defaults merged with Firestore overrides.
  const resolved = useMemo(() => getResolvedCheckinItems(config), [config]);
  const resolvedById = useMemo(() => {
    const m = {};
    resolved.forEach((it) => { m[it.id] = it; });
    return m;
  }, [resolved]);

  // Save the whole config (merge:true; we always write complete item objects).
  const saveConfig = (next) => {
    setConfig(next); // optimistic
    setSaveStatus('saving');
    const ref = doc(db, 'config', CHECKIN_CONFIG_DOC);
    setDoc(ref, next, { merge: true })
      .then(() => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus((s) => (s === 'saved' ? null : s)), 2500);
      })
      .catch((err) => {
        console.error('Save checkinPointPeople failed:', err);
        setSaveStatus({ error: err.message || 'Save failed' });
      });
  };

  // Patch one item. We start from the RESOLVED item so the first edit persists
  // the full {signers, department, audience, required} object, defaults and all.
  const updateItem = (itemId, patch) => {
    const base = resolvedById[itemId];
    if (!base) return;
    const merged = {
      signers: base.signers,
      department: base.department,
      audience: base.audience,
      required: base.required,
      ...patch,
    };
    saveConfig({
      ...config,
      items: { ...(config?.items || {}), [itemId]: merged },
    });
  };

  const toggleAudience = (itemId, tag) => {
    const current = resolvedById[itemId]?.audience || ['all'];
    let next;
    if (tag === 'all') {
      next = ['all'];
    } else {
      const set = new Set(current.filter((a) => a !== 'all'));
      if (set.has(tag)) set.delete(tag);
      else set.add(tag);
      next = set.size === 0 ? ['all'] : Array.from(set);
    }
    updateItem(itemId, { audience: normalizeAudience(next) });
  };

  const totalAssigned = useMemo(() => {
    const set = new Set();
    resolved.forEach((it) => (it.signers || []).forEach((uid) => set.add(uid)));
    return set.size;
  }, [resolved]);

  const requiredCount = useMemo(
    () => resolved.filter((it) => it.required).length,
    [resolved],
  );

  return (
    <div style={styles.accordion}>
      <div
        style={styles.header(open)}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
      >
        <div style={styles.headerLeft}>
          <span style={styles.icon}>✅</span>
          <div>
            <h3 style={styles.title}>Student Check-In Permissions</h3>
            <p style={styles.subtitle}>
              {CHECKIN_ITEMS.length} items · {requiredCount} required
              {totalAssigned > 0 && ` · ${totalAssigned} staff assigned`}
            </p>
          </div>
        </div>
        <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>

      {open && (
        <div style={styles.body}>
          {/* Shared datalist of department suggestions across all rows */}
          <datalist id="checkin-dept-suggestions">
            {Array.from(new Set(CHECKIN_ITEMS.map((i) => i.department))).map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>

          <div style={styles.intro}>
            For each check-in item, set who can sign it off, the owning
            department (for contact), which students it applies to, and whether
            it's required. Staff assigned here will be able to check students
            off for that item in the Student Check-In tool. The audience filter
            controls which students the item even shows up for — e.g. the{' '}
            <strong>Boarding Student Permission Form</strong> only appears for
            boarders, <strong>Immigration/Visa Clearance</strong> only for
            international students.
          </div>

          {(staffLoading || configLoading) && (
            <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>Loading…</div>
          )}

          {!staffLoading && !configLoading && (
            <>
              {resolved.map((item, idx) => {
                const isLast = idx === resolved.length - 1;
                return (
                  <div
                    key={item.id}
                    style={{ ...styles.itemRow, ...(isLast ? styles.itemRowLast : {}) }}
                  >
                    <div style={styles.itemHeader}>
                      <div>
                        <h4 style={styles.itemLabel}>{item.label}</h4>
                        {item.description && (
                          <p style={styles.itemDesc}>{item.description}</p>
                        )}
                      </div>
                      <div style={styles.requiredWrap}>
                        <span style={styles.requiredLabel(item.required)}>
                          {item.required ? 'Required' : 'Optional'}
                        </span>
                        <Toggle
                          on={item.required}
                          onChange={() => updateItem(item.id, { required: !item.required })}
                        />
                      </div>
                    </div>

                    <div style={styles.twoCol}>
                      <div>
                        <p style={styles.fieldLabel}>Department (contact)</p>
                        <DepartmentInput
                          value={item.department}
                          onCommit={(v) => updateItem(item.id, { department: v })}
                        />
                      </div>
                      <div>
                        <p style={styles.fieldLabel}>Applies to</p>
                        <div style={styles.audienceRow}>
                          {CHECKIN_AUDIENCES.map((aud) => {
                            const on = item.audience.includes(aud.id);
                            return (
                              <button
                                key={aud.id}
                                type="button"
                                style={styles.audiencePill(on)}
                                onClick={() => toggleAudience(item.id, aud.id)}
                              >
                                {aud.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <p style={styles.fieldLabelGold}>Staff who can check this off</p>
                    <StaffPicker
                      allStaff={allStaff}
                      selectedUids={item.signers}
                      onChange={(uids) => updateItem(item.id, { signers: uids })}
                      placeholder="Add staff…"
                    />
                  </div>
                );
              })}

              {saveStatus === 'saving' && (
                <div style={{ ...styles.saveStatus, ...styles.saveStatusSaving }}>Saving…</div>
              )}
              {saveStatus === 'saved' && (
                <div style={{ ...styles.saveStatus, ...styles.saveStatusSuccess }}>✓ Saved</div>
              )}
              {saveStatus && saveStatus.error && (
                <div style={{ ...styles.saveStatus, ...styles.saveStatusError }}>
                  Save failed: {saveStatus.error}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}